import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { EARN_ASSET_STATUS, EARN_DASHBOARD_STATE, EarnAsset } from '../types'
import {
  calculateRetainedEarningsPercentage,
  formatAPY,
  formatUsd,
  formatWholePercentage,
  toMyEarnAssets,
} from '../utils/utils'
import { useEarnDashboardQuery } from './api/useEarnDashboardQuery'
import { useEarnStatisticsQuery } from './api/useEarnStatisticsQuery'
import { useIsDirectEarning } from './useIsDirectEarning'

export interface UseEarnPageDataHandlers {
  onDeposit: (asset: EarnAsset) => void
  onWithdraw: (asset: EarnAsset) => void
}

export const useEarnPageData = ({ onDeposit, onWithdraw }: UseEarnPageDataHandlers) => {
  const { t } = useTranslation()
  const { data, isLoading } = useEarnDashboardQuery()
  const { data: statistics, isLoading: isStatisticsLoading } = useEarnStatisticsQuery()
  const isDirectEarning = useIsDirectEarning()
  const canManageDistribution = !!useMappedRoles()?.earn?.canDistribute

  const statisticsByCurrency = useMemo(
    () => Object.fromEntries((statistics?.currencies ?? []).map(stat => [stat.currency, stat])),
    [statistics]
  )

  const { earnAssets, assets } = useMemo(() => {
    const earnAssets =
      data?.state === EARN_DASHBOARD_STATE.READY ||
      data?.state === EARN_DASHBOARD_STATE.NO_PORTFOLIOS
        ? data.earnAssets
        : []

    return {
      earnAssets,
      assets: earnAssets.map(toMyEarnAssets(t, onDeposit, onWithdraw, statisticsByCurrency)),
    }
  }, [data, onDeposit, onWithdraw, t, statisticsByCurrency])

  const activeEarnAsset = earnAssets.find(asset => asset.status === EARN_ASSET_STATUS.ACTIVE)

  const merchantStatistics = useMemo(
    () =>
      statistics?.currencies.find(stat => stat.currency === activeEarnAsset?.asset) ??
      statistics?.currencies[0],
    [statistics, activeEarnAsset]
  )

  const epcTakesEarn = activeEarnAsset?.epcTakesEarn ?? false

  const readyData = data?.state === EARN_DASHBOARD_STATE.READY ? data : undefined

  const retainedEarningsPercentage = formatAPY(
    calculateRetainedEarningsPercentage(epcTakesEarn, readyData)
  )

  const optInRateLabel = t('earn.optInRateLabel', {
    rate: formatWholePercentage(Number(merchantStatistics?.optIn.rate ?? 0) * 100),
    count: merchantStatistics?.optIn.optedInCustomers ?? 0,
  })

  return {
    dashboardState: data?.state,
    featuredProduct:
      data?.state === EARN_DASHBOARD_STATE.NO_WALLETS ? data.featuredProduct : undefined,
    isLoading,
    isStatisticsLoading,
    isDirectEarning,
    canManageDistribution,
    assets,
    epcTakesEarn,
    summary: {
      allocatedFunds: formatUsd(readyData?.fundsOverview.allocatedFunds),
      availableFunds: formatUsd(statistics?.totals.merchantMovableBalance),
      totalAllTimeEarnings: formatUsd(readyData?.totalEarnings.total),
    },
    totalEarnings: {
      allocationEarnings: formatUsd(readyData?.totalEarnings.fromMyAllocation),
      customerEarnings: formatUsd(readyData?.totalEarnings.fromCustomers),
      totalEarnings: formatUsd(readyData?.totalEarnings.total),
    },
    customerRewardsProgram: {
      totalBalance: formatUsd(statistics?.totals.customerEarningBalance),
      retainedEarningsPercentage,
      optInRateLabel,
      distributedEarnings: formatUsd(readyData?.customerRewardsProgram.distributedEarnings),
      optedInTotal: formatUsd(readyData?.customerRewardsProgram.optedInTotal),
      remainingTotal: formatUsd(readyData?.customerRewardsProgram.remainingTotal),
    },
  }
}
